// EditLabelsTab.tsx
import React, { useRef, useEffect, useState, useCallback } from "react"
import { Table, Tag, Tooltip, type GetRef } from "antd"
import type { ColumnsType } from "antd/es/table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faExclamationTriangle,
  faFileExport,
  faFileArrowDown,
  faLock,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { EditorRef } from "types/questionnaire.types"
import { LabelItem, LabelType } from "utils/labelUtils"
import { useLabelsManager } from "features/QuestionnaireEditor/hooks/Labels/useLabelsManager"
import { useValidation } from "features/QuestionnaireEditor/hooks/Labels/useValidation"
import { LabelsHeader } from "./LabelsHeader.component"
import { EditableTextArea } from "./EditableTextArea.component"
import { QuestionLabelEditor } from "./QuestionLabelEditor.component"
import { ReferenceButtons } from "./ReferenceButtons.component"

/** Fixed widths for the non-content columns (content fills the rest). */
const NAME_W = 180
const TYPE_W = 130
const REFS_W = 140

/** Tag colour per label type. */
const getTypeColor = (type: LabelType): string => {
  switch (type) {
    case LabelType.QUESTION:
      return "blue"
    case LabelType.LIST:
      return "green"
    case LabelType.RANGE_LIST:
      return "orange"
    case LabelType.EXCEPTIONS:
      return "red"
    default:
      return "default"
  }
}

/**
 * Props interface for the EditLabelsTab component
 */
interface EditLabelsTabProps {
  /** Ref to the editor component */
  editorRef: React.RefObject<EditorRef | null>
  /** Function to set modal visibility */
  setIsModalVisible: (visible: boolean) => void
  /** Function to scroll to a specific line in the editor */
  scrollToLine?: (lineNumber: number) => void
  /** The available content height in pixels */
  contentHeight: number
  /** Optional label name to jump to when modal opens */
  jumpToLabel?: string | null
  /** Callback when jump is complete */
  onJumpComplete?: () => void
}

/**
 * EditLabelsTab is the editable label manager: question labels, lists, range
 * lists and exceptions. Renaming a label rewrites every occurrence across the
 * questionnaire — including <<references>>, and PER/FROM/RANDOMIZE list uses.
 *
 * The table is virtualized (antd Table `virtual`) so only the visible rows are
 * rendered — opening stays fast even with hundreds of labels.
 *
 * @component
 */
const EditLabelsTab: React.FC<EditLabelsTabProps> = ({
  editorRef,
  setIsModalVisible,
  scrollToLine,
  contentHeight,
  jumpToLabel,
  onJumpComplete,
}) => {
  const {
    labels,
    filteredLabels,
    hasUnsavedChanges,
    isLoading,
    filterType,
    handleFilterChange,
    handleLabelChange: baseHandleLabelChange,
    handleUpdateQuestionnaire,
    handleExportToExcel,
    handleImportFromExcel,
  } = useLabelsManager({ editorRef, setIsModalVisible })

  const { validationErrors, validateLabel } = useValidation()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const tableRef = useRef<GetRef<typeof Table>>(null)

  // Confirmation modal for closing with unsaved changes
  const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false)
  // Row to briefly highlight after a jump-to-label
  const [highlightName, setHighlightName] = useState<string | null>(null)

  // Measure the table area so the virtual list gets a pixel height/width
  // (antd virtual needs a numeric scroll.y and column widths). A ResizeObserver
  // keeps it correct as the modal is resized.
  const [bodySize, setBodySize] = useState<{ width: number; height: number }>({
    width: 800,
    height: 400,
  })
  const resizeObsRef = useRef<ResizeObserver | null>(null)
  const tableWrapRef = useCallback((node: HTMLDivElement | null) => {
    resizeObsRef.current?.disconnect()
    if (node) {
      const update = (): void =>
        setBodySize({ width: node.clientWidth, height: node.clientHeight })
      const ro = new ResizeObserver(update)
      ro.observe(node)
      resizeObsRef.current = ro
      update()
    }
  }, [])
  useEffect(() => () => resizeObsRef.current?.disconnect(), [])

  /**
   * Jump to a label: scroll the virtual list to its row and highlight it.
   */
  useEffect(() => {
    if (!jumpToLabel || isLoading) return
    const index = filteredLabels.findIndex((l) => l.name === jumpToLabel)
    if (index >= 0) {
      const t1 = setTimeout(() => {
        tableRef.current?.scrollTo({ index })
        setHighlightName(jumpToLabel)
      }, 50)
      const t2 = setTimeout(() => setHighlightName(null), 2200)
      onJumpComplete?.()
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
      }
    }
    onJumpComplete?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToLabel, isLoading])

  /**
   * Label change handler with validation.
   */
  const handleLabelChange = (id: string, newValue: string): void => {
    baseHandleLabelChange(id, newValue)
    const label = labels.find((l) => l.id === id)
    if (label) {
      validateLabel(id, newValue, label.type)
    }
  }

  const handleUpdate = async (): Promise<void> => {
    await handleUpdateQuestionnaire(validationErrors)
  }

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0]
    if (file) {
      handleImportFromExcel(file)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const handleImportClick = (): void => {
    fileInputRef.current?.click()
  }

  /**
   * Closes the modal, confirming first if there are unsaved changes.
   */
  const handleClose = (): void => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true)
    } else {
      setIsModalVisible(false)
    }
  }

  /**
   * Renders the editable content cell for a row.
   */
  const renderContentCell = (item: LabelItem): React.ReactNode => {
    if (item.type === LabelType.EXCEPTIONS) {
      return (
        <Tooltip
          title="EXCEPTIONS list is read-only and cannot be edited here. To modify exceptions, edit them directly in the questionnaire editor."
          placement="top"
        >
          <div className="p-2 bg-gray-100 rounded border border-gray-300 font-mono text-sm whitespace-pre-wrap cursor-not-allowed relative">
            <div className="absolute top-2 right-2 text-gray-500">
              <FontAwesomeIcon icon={faLock} className="text-xs" />
            </div>
            {item.content}
          </div>
        </Tooltip>
      )
    }

    return (
      <div className="flex flex-col gap-1">
        {item.type === LabelType.QUESTION ? (
          <QuestionLabelEditor
            value={item.content}
            onChange={(newValue: string) => handleLabelChange(item.id, newValue)}
          />
        ) : (
          <EditableTextArea
            id={item.id}
            value={item.content}
            onChange={(newValue: string) => handleLabelChange(item.id, newValue)}
          />
        )}
        {validationErrors[item.id] && (
          <div className="flex items-center gap-1 text-red-600 text-xs bg-red-50 p-2 rounded border border-red-200">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-[12px]" />
            <span>{validationErrors[item.id]}</span>
          </div>
        )}
      </div>
    )
  }

  const contentWidth = Math.max(
    280,
    bodySize.width - NAME_W - TYPE_W - REFS_W - 16
  )

  const columns: ColumnsType<LabelItem> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: NAME_W,
      render: (name: string) => (
        <span className="font-mono text-sm break-words">{name}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: TYPE_W,
      render: (type: LabelType) => <Tag color={getTypeColor(type)}>{type}</Tag>,
    },
    {
      title: "Content",
      key: "content",
      width: contentWidth,
      render: (_: unknown, item: LabelItem) => renderContentCell(item),
    },
    {
      title: "References",
      key: "references",
      width: REFS_W,
      render: (_: unknown, item: LabelItem) => (
        <ReferenceButtons
          item={item}
          scrollToLine={scrollToLine}
          setIsModalVisible={setIsModalVisible}
        />
      ),
    },
  ]

  /**
   * Renders empty state
   */
  const renderEmptyState = () => (
    <div className="py-8">
      <p className="text-center text-gray-500">
        No labels or lists found in the questionnaire.
      </p>
      <p className="text-center text-gray-400 mt-2">
        Add question labels (Q Label:), lists (LIST Name), range lists (RANGE
        LIST Name: 1..5), or exceptions (EXCEPTIONS) to manage them here.
      </p>
    </div>
  )

  return (
    <div className="bg-white flex flex-col" style={{ height: contentHeight }}>
      <div className="flex-grow min-h-0 flex flex-col px-1 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <Loader fontSize={48} />
            Loading labels and lists...
          </div>
        ) : !labels || labels.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            <LabelsHeader
              filterType={filterType}
              filteredCount={filteredLabels.length}
              onFilterChange={handleFilterChange}
            />
            {filteredLabels.length > 0 ? (
              <div ref={tableWrapRef} className="flex-1 min-h-0">
                <Table<LabelItem>
                  ref={tableRef}
                  virtual
                  size="small"
                  columns={columns}
                  dataSource={filteredLabels}
                  rowKey="id"
                  pagination={false}
                  scroll={{ y: Math.max(120, bodySize.height - 44) }}
                  rowClassName={(record) =>
                    record.name === highlightName ? "bg-blue-100" : ""
                  }
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">
                No labels or lists match the selected filter.
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between mt-4 space-x-2 bg-white pt-2 pb-4 px-4 border-t">
        <div className="flex items-center space-x-2">
          <DataQButton
            type="secondary"
            isDisabled={isLoading || !labels || labels.length === 0}
            onClick={handleExportToExcel}
          >
            <FontAwesomeIcon icon={faFileExport} />
            Export to Excel
          </DataQButton>
          <DataQButton
            type="secondary"
            isDisabled={isLoading || !labels || labels.length === 0}
            onClick={handleImportClick}
          >
            <FontAwesomeIcon icon={faFileArrowDown} />
            Import from Excel
          </DataQButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <div className="flex items-center text-yellow-600">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              <span>You have unsaved changes</span>
            </div>
          )}
          <DataQButton
            type="primary"
            isDisabled={isLoading || !labels || labels.length === 0}
            onClick={handleUpdate}
          >
            Update Questionnaire
          </DataQButton>
          <DataQButton type="secondary" onClick={handleClose}>
            {hasUnsavedChanges ? "Cancel" : "Close"}
          </DataQButton>
        </div>
      </div>

      <ConfirmationModal
        isVisible={showCloseConfirm}
        title="Discard unsaved changes?"
        message="You have changes that haven't been applied to the questionnaire yet. Close anyway?"
        confirmButtonText="Discard & close"
        confirmButtonType="danger"
        cancel={() => setShowCloseConfirm(false)}
        confirm={() => {
          setShowCloseConfirm(false)
          setIsModalVisible(false)
        }}
      />
    </div>
  )
}

export default EditLabelsTab
